import React, { useState, useEffect } from 'react';
import { AlertCircle, Mail, ChevronDown, ChevronRight, Users, Loader, User, Phone, Hash, Eye } from 'lucide-react';
import { useSelector } from 'react-redux';
// import services
import { getChildNode, getTreeInfo } from '../services/user.service';

const initialInfo = {
    leftTeams: 0,
    rightTeams: 0,
    totalTeams: 0,
    binaryIncome: 0
};

const BinaryTree = () => {
    // hooks
    const { userId } = useSelector((state) => state?.auth);
    // state
    const [treeInfo, setTreeInfo] = useState(initialInfo);
    const [treeNodes, setTreeNodes] = useState({});
    const [expandedNodes, setExpandedNodes] = useState(new Set([0]));
    const [loadingNodes, setLoadingNodes] = useState(new Set());
    const [initialLoading, setInitialLoading] = useState(true);
    const [copiedLink, setCopiedLink] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    // Fetch node data from API
    const fetchNodeData = async (nodeId) => {
        try {
            setLoadingNodes(prev => new Set([...prev, nodeId]));

            const { status, data } = await getChildNode(nodeId);

            setTreeNodes(prev => ({
                ...prev,
                [nodeId]: data
            }));

            setLoadingNodes(prev => {
                const newSet = new Set(prev);
                newSet.delete(nodeId);
                return newSet;
            });

            return data;
        } catch (error) {
            console.error('Error fetching node:', error);
            setLoadingNodes(prev => {
                const newSet = new Set(prev);
                newSet.delete(nodeId);
                return newSet;
            });

            return {
                key: nodeId,
                left: 0,
                right: 0,
                parent: 0,
                upliner: 0
            };
        }
    };

    const handleViewClick = (e, node) => {
        e.stopPropagation();
        setSelectedNode(node);
        setTooltipPosition({
            x: e.clientX,
            y: e.clientY
        });
    };

    const closeTooltip = () => {
        setSelectedNode(null);
    };

    // Tooltip component
    const NodeTooltip = ({ node, position, onClose }) => {
        if (!node) return null;

        return (
            <div
                className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 min-w-[200px] transform -translate-x-1/2 -translate-y-full"
                style={{
                    left: `${position.x}px`,
                    top: `${position.y - 10}px`,
                }}
            >
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1 w-4 h-4 bg-white border-r border-b border-gray-200 rotate-45"></div>

                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    &times;
                </button>

                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-cyan-600" />
                        <span className="font-semibold text-gray-800">User Details</span>
                    </div>

                    <div className="border-t border-gray-100 pt-2 space-y-2">
                        <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-gray-500" />
                            <span className="text-sm text-gray-600">
                                {node?.userName || 'N/A'}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-gray-500" />
                            <span className="text-sm text-gray-600">
                                {node?.phone || 'N/A'}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 text-gray-500" />
                            <span className="text-sm text-gray-600">
                                {node?.email || 'N/A'}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Hash className="w-3 h-3 text-gray-500" />
                            <span className="text-sm text-gray-600">
                                {node.userCode || 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const TreeNode = ({ nodeId, depth = 0 }) => {
        const node = treeNodes[nodeId];
        console.log(node, 'nodenode');
        const isLoading = loadingNodes.has(nodeId);

        if (!node && !isLoading) {
            return null;
        }

        if (isLoading) {
            return (
                <div className="flex flex-col items-center">
                    <div className="bg-white rounded-lg p-4 shadow-lg min-w-[140px] border-2 border-cyan-300">
                        <div className="text-center">
                            <Loader className="w-8 h-8 text-cyan-500 mx-auto animate-spin" />
                            <div className="text-xs text-gray-500 mt-2">Loading...</div>
                        </div>
                    </div>
                </div>
            );
        }

        const hasLeftChild = node.left !== 0;
        const hasRightChild = node.right !== 0;
        const canExpand = hasLeftChild || hasRightChild;
        const isExpanded = expandedNodes.has(nodeId);
        const level = node?.level;
        const income = node?.amountPaid === true ? 2000 : 0;

        const leftCopied = copiedLink === node.leftRefCode;
        const rightCopied = copiedLink === node.rightRefCode;

        const toggleNode = async () => {
            if (!canExpand) return;

            const newExpanded = new Set(expandedNodes);

            if (newExpanded.has(nodeId)) {
                newExpanded.delete(nodeId);
                setExpandedNodes(newExpanded);
            } else {
                newExpanded.add(nodeId);
                setExpandedNodes(newExpanded);

                if (hasLeftChild && !treeNodes[node.left]) {
                    await fetchNodeData(node.left);
                }

                if (hasRightChild && !treeNodes[node.right]) {
                    await fetchNodeData(node.right);
                }
            }
        };

        return (
            <div className="flex flex-col items-center">
                {/* Node Card */}
                <div
                    className={`relative bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-lg p-4 shadow-xl min-w-[140px] ${
                        canExpand ? 'cursor-pointer hover:scale-105 hover:shadow-2xl' : ''
                    } transition-all border-2 border-cyan-300`}
                    onClick={toggleNode}
                >
                    {/* View Icon */}
                    <button
                        onClick={(e) => handleViewClick(e, node)}
                        className="absolute -top-2 -right-2 bg-white hover:bg-gray-100 text-cyan-600 rounded-full p-1 shadow-lg transition-all hover:scale-110"
                        title="View User Details"
                    >
                        <Eye className="w-4 h-4" />
                    </button>

                    <div className="text-center">
                        <div className="text-xs opacity-90 mb-1 font-semibold">ID: {node.key}</div>
                        <Users className="w-8 h-8 mx-auto mb-2" />
                        <div className="text-xs font-semibold bg-white/20 rounded px-2 py-1 mb-2">
                            Level {level}
                        </div>
                    </div>

                    {/* Expand/Collapse Icon */}
                    {canExpand && (
                        <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-white rounded-full p-1 shadow-lg">
                            {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-cyan-600" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-cyan-600" />
                            )}
                        </div>
                    )}

                    {/* No children indicator */}
                    {!canExpand && (
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                            <div className="bg-gray-400 rounded-full w-3 h-3 border-2 border-white"></div>
                        </div>
                    )}
                </div>

                {/* Children - Fixed left/right positioning */}
                {isExpanded && canExpand && (
                    <div className="mt-10 flex gap-12 relative justify-center">
                        {/* Vertical line from parent */}
                        <div className="absolute top-0 left-1/2 w-0.5 h-8 bg-cyan-400 -translate-x-1/2 -translate-y-8"></div>
                        
                        {/* Horizontal line connecting children */}
                        {hasLeftChild && hasRightChild && (
                            <div 
                                className="absolute top-8 h-0.5 bg-cyan-400 -translate-y-8"
                                style={{
                                    left: '25%',
                                    right: '25%'
                                }}
                            ></div>
                        )}

                        {/* LEFT SIDE - Always render in first position */}
                        <div className="flex flex-col items-center relative min-w-[200px]">
                            {hasLeftChild ? (
                                <>
                                    {/* Vertical line to child */}
                                    <div className="absolute top-0 left-1/2 w-0.5 h-8 bg-cyan-400 -translate-x-1/2 -translate-y-8"></div>
                                    
                                    {/* Label */}
                                    <div className="text-xs font-semibold text-white bg-cyan-600 px-3 py-1 rounded-full mb-3 shadow-md">
                                        LEFT
                                    </div>

                                    {/* Show loader or TreeNode */}
                                    {loadingNodes.has(node.left) ? (
                                        <div className="flex flex-col items-center">
                                            <div className="bg-white rounded-lg p-4 shadow-lg min-w-[140px] border-2 border-cyan-300 animate-pulse">
                                                <div className="text-center">
                                                    <Loader className="w-8 h-8 text-cyan-500 mx-auto animate-spin" />
                                                    <div className="text-xs text-gray-500 mt-2">Loading...</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <TreeNode nodeId={node.left} depth={depth + 1} />
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="text-xs font-semibold text-gray-600 bg-gray-200 px-3 py-1 rounded-full mb-3">
                                        LEFT (Empty)
                                    </div>
                                    <div className="w-32 h-32 border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center bg-white opacity-50">
                                        <span className="text-gray-500 text-xs">Available</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* RIGHT SIDE - Always render in second position */}
                        <div className="flex flex-col items-center relative min-w-[200px]">
                            {hasRightChild ? (
                                <>
                                    {/* Vertical line to child */}
                                    <div className="absolute top-0 left-1/2 w-0.5 h-8 bg-cyan-400 -translate-x-1/2 -translate-y-8"></div>
                                    
                                    {/* Label */}
                                    <div className="text-xs font-semibold text-white bg-purple-600 px-3 py-1 rounded-full mb-3 shadow-md">
                                        RIGHT
                                    </div>

                                    {/* Show loader or TreeNode */}
                                    {loadingNodes.has(node.right) ? (
                                        <div className="flex flex-col items-center">
                                            <div className="bg-white rounded-lg p-4 shadow-lg min-w-[140px] border-2 border-purple-300 animate-pulse">
                                                <div className="text-center">
                                                    <Loader className="w-8 h-8 text-purple-500 mx-auto animate-spin" />
                                                    <div className="text-xs text-gray-500 mt-2">Loading...</div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <TreeNode nodeId={node.right} depth={depth + 1} />
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="text-xs font-semibold text-gray-600 bg-gray-200 px-3 py-1 rounded-full mb-3">
                                        RIGHT (Empty)
                                    </div>
                                    <div className="w-32 h-32 border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center bg-white opacity-50">
                                        <span className="text-gray-500 text-xs">Available</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const initialCall = async () => {
        try {
            const { status, info } = await getTreeInfo();
            if (status === true) {
                setTreeInfo({ ...treeInfo, ...info });
            }
        } catch (error) {
            console.error('Error fetching tree info:', error);
        }
    };

    useEffect(() => {
        const loadInitialNode = async () => {
            await fetchNodeData(userId);
            setInitialLoading(false);
        };

        if (userId) loadInitialNode();
    }, [userId]);

    useEffect(() => {
        initialCall();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (selectedNode && !e.target.closest('.tooltip-content')) {
                closeTooltip();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectedNode]);

    if (initialLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-transparent">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-cyan-500 mx-auto animate-spin mb-4" />
                    <p className="text-gray-600">Loading tree...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
            {selectedNode && (
                <div className="tooltip-content">
                    <NodeTooltip
                        node={selectedNode}
                        position={tooltipPosition}
                        onClose={closeTooltip}
                    />
                </div>
            )}
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="p-8">
                        <div className="mb-6">
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">Binary Tree Visualization</h1>
                            <p className="text-gray-600">Explore your network structure</p>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 mb-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-indigo-600">{treeInfo?.leftTeams}</div>
                                    <div className="text-sm text-gray-600">Left Members</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-purple-600">{treeInfo?.rightTeams}</div>
                                    <div className="text-sm text-gray-600">Right Members</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">
                                        ₹{treeInfo?.total_binary_bonus?.toLocaleString()}
                                    </div>
                                    <div className="text-sm text-gray-600">Binary Income</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-pink-600">
                                        {treeInfo?.level}
                                    </div>
                                    <div className="text-sm text-gray-600">Level</div>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto pb-8">
                            <div className="min-w-max flex justify-center">
                                <TreeNode nodeId={userId} depth={0} />
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-blue-800">
                                    <strong>Interactive Tree:</strong> Click on nodes to expand and see the binary structure.
                                    The tree shows how members are distributed on the left and right sides at each level.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BinaryTree;